import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import type { Money } from "../../../../apps/frontend/graphql";
import { serialize } from "../components/renderer/data";
import { useConfig } from "../context/ConfigContext";
import { useCabinetStore } from "../context/StoreContext";
import { useCanvasStore } from "./useCanvasStore";

type PriceType =
	| {
			variant: "error" | "calculating";
			money?: never;
	  }
	| {
			variant: "default";
			money: Money;
	  };

export function usePrice() {
	const { state } = useCabinetStore();
	const { setAllCompartmentsOpen } = useCanvasStore();
	const config = useConfig();
	const [price, setPrice] = useState<PriceType>({ variant: "calculating" });
	const [debouncedState] = useDebounceValue(state, 1000);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Should run when state changes
	useEffect(() => {
		setPrice({ variant: "calculating" });
	}, [state]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Should run when debouncedState changes
	useEffect(() => {
		if (config.calculatePrice) {
			const json = serialize(debouncedState);

			config
				.calculatePrice(json)
				.then((p) => {
					if (typeof p === "string") {
						setAllCompartmentsOpen(true);
						setPrice({ variant: p });
					} else {
						setPrice({ variant: "default", money: p });
					}
				})
				.catch(console.error);
		}
	}, [debouncedState]);

	return price;
}

export function getMoneyValue(money: Money) {
	return money.display === "INCLUDING_TAX" ? money.includingTax : money.excludingTax;
}
